import { resolveEventStaffIds } from '../calendarUtils';
import { parseDateTimeInputValue } from '../../../shared/utils/localization';
import type { CalendarEvent, CalendarEventStatus } from '../types/Calendar';

export type CalendarEventListPanelFilters = {
  status: CalendarEventStatus | '';
  staffId: string;
  contactId: string;
  search: string;
};

export type CalendarEventListSortField =
  | 'start'
  | 'end'
  | 'title'
  | 'status'
  | 'room'
  | 'staff'
  | 'contact'
  | 'notes';

export type CalendarEventListSortDirection = 'asc' | 'desc';

export type CalendarEventListSort = {
  field: CalendarEventListSortField;
  direction: CalendarEventListSortDirection;
};

export type CalendarEventListSortContext = {
  getRoomDisplayName: (roomId?: string) => string;
  getStaffNames: (staffIds?: string[]) => string;
  getContactLabel: (contactId?: string) => string;
  getEventDisplayTitle: (event: Pick<CalendarEvent, 'title' | 'appointmentTypeId'>) => string;
  getStatusLabel: (status: CalendarEventStatus) => string;
};

export const DEFAULT_CALENDAR_EVENT_LIST_PANEL_FILTERS: CalendarEventListPanelFilters = {
  status: '',
  staffId: '',
  contactId: '',
  search: '',
};

export const DEFAULT_CALENDAR_EVENT_LIST_SORT: CalendarEventListSort = {
  field: 'start',
  direction: 'asc',
};

export const buildCalendarEventListRange = (
  startDate: string,
  endDate: string,
): { fromLocal: string; toLocal: string } | null => {
  const fromLocal = `${startDate.trim()}T00:00`;
  const toLocal = `${endDate.trim()}T23:59`;
  const from = parseDateTimeInputValue(fromLocal);
  const to = parseDateTimeInputValue(toLocal);

  if (!from || !to || from.getTime() > to.getTime()) {
    return null;
  }

  return { fromLocal, toLocal };
};

export const applyCalendarEventListPanelFilters = (  events: CalendarEvent[],
  filters: CalendarEventListPanelFilters,
): CalendarEvent[] => {
  const normalizedSearch = filters.search.trim().toLowerCase();

  return events.filter((event) => {
    if (filters.status && event.status !== filters.status) {
      return false;
    }

    if (filters.contactId && event.contactId !== filters.contactId) {
      return false;
    }

    if (filters.staffId) {
      const staffIds = resolveEventStaffIds(event);
      if (!staffIds.includes(filters.staffId)) {
        return false;
      }
    }

    if (normalizedSearch) {
      const haystack = `${event.title || ''} ${event.notes || ''}`.toLowerCase();
      if (!haystack.includes(normalizedSearch)) {
        return false;
      }
    }

    return true;
  });
};

const compareStrings = (left: string, right: string) => (
  left.localeCompare(right, undefined, { sensitivity: 'base' })
);

export const sortCalendarEventList = (
  events: CalendarEvent[],
  sort: CalendarEventListSort,
  context: CalendarEventListSortContext,
): CalendarEvent[] => {
  const directionMultiplier = sort.direction === 'asc' ? 1 : -1;

  return [...events].sort((left, right) => {
    let comparison = 0;

    switch (sort.field) {
      case 'start':
        comparison = new Date(left.startAtUtc).getTime() - new Date(right.startAtUtc).getTime();
        break;
      case 'end':
        comparison = new Date(left.endAtUtc).getTime() - new Date(right.endAtUtc).getTime();
        break;
      case 'title':
        comparison = compareStrings(
          context.getEventDisplayTitle(left),
          context.getEventDisplayTitle(right),
        );
        break;
      case 'status':
        comparison = compareStrings(
          context.getStatusLabel(left.status),
          context.getStatusLabel(right.status),
        );
        break;
      case 'room':
        comparison = compareStrings(
          context.getRoomDisplayName(left.roomId),
          context.getRoomDisplayName(right.roomId),
        );
        break;
      case 'staff':
        comparison = compareStrings(
          context.getStaffNames(resolveEventStaffIds(left)),
          context.getStaffNames(resolveEventStaffIds(right)),
        );
        break;
      case 'contact':
        comparison = compareStrings(
          context.getContactLabel(left.contactId),
          context.getContactLabel(right.contactId),
        );
        break;
      case 'notes':
        comparison = compareStrings(left.notes || '', right.notes || '');
        break;
      default:
        comparison = 0;
    }

    if (comparison === 0) {
      return new Date(left.startAtUtc).getTime() - new Date(right.startAtUtc).getTime();
    }

    return comparison * directionMultiplier;
  });
};
